/**
 * <c-vehicle-selector> — step-by-step "wizard" flow.
 *
 * Data source depends on data-source (set by the snippet based on page
 * context — this component is reused on both the homepage and collection
 * pages):
 *   - "graphql" (default / homepage): a modernized rebuild of
 *     assets/other-makes.js's paginated Storefront GraphQL query against
 *     the same product metafield shape, cached in localStorage so a repeat
 *     visit skips the network call. Full catalog.
 *   - "collection": no network request at all — the collection's matching
 *     products are pre-rendered server-side into a JSON <script> island
 *     (see snippets/c-vehicle-selector.liquid), read directly. Scoped to
 *     just that collection.
 * Either source feeds the exact same buildTree()/optionsForStep() pipeline
 * below, so the wizard UI and its Make -> Model -> Body & Year -> Engine
 * flow behave identically regardless of where the data came from.
 *
 * UI layer is a single-step wizard: a "Find Your Vehicle" trigger opens it;
 * only one field (Make, then Model, then Body & Year, then Engine) is ever
 * on screen at a time, with a breadcrumb of completed steps above it and a
 * reset icon to start over. Each step transition is a CSS
 * fade-out/fade-in-and-slide handled via .is-leaving/.is-entering classes
 * on the stage element (see c-vehicle-selector.css) — this file only ever
 * toggles those classes and swaps innerHTML between transitions, it never
 * touches inline styles.
 *
 * Scoped to its own subtree (querySelector runs on `this`, not
 * `document`), so multiple instances on a page never cross-fire.
 */
(() => {
  const STEP_ORDER = ['make', 'model', 'body', 'engine'];

  const STEP_LABEL = {
    make: 'Select Make',
    model: 'Select Model',
    body: 'Select Body & Year',
    engine: 'Select Engine',
  };

  const STEP_LABEL_SHORT = {
    make: 'Make',
    model: 'Model',
    body: 'Body & Year',
    engine: 'Engine',
  };

  const STAGE_TRANSITION_MS = 220;

  function getMinMaxYears(yearKeys) {
    const parsed = yearKeys.map((year) => {
      const parts = String(year).split('-');
      const start = parseInt(parts[0], 10);
      const end = parts[1] && /^\d+$/.test(parts[1]) ? parseInt(parts[1], 10) : null;
      return [start, end];
    });
    const starts = parsed.map((pair) => pair[0]).filter((n) => !Number.isNaN(n));
    const ends = parsed.map((pair) => pair[1]).filter((n) => n !== null);
    const min = starts.length ? Math.min(...starts) : null;
    const max = ends.length ? Math.max(...ends) : null;
    return { min, max };
  }

  function displayRange(min, max) {
    if (min === null) return '';
    return max ? `${min}–${max}` : `${min}+`;
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => {
      const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
      return map[char];
    });
  }

  class VehicleSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.vsInitialized) return;
      this.dataset.vsInitialized = 'true';

      this.dataSource = this.dataset.source || 'graphql'; // 'graphql' | 'collection'
      this.token = this.dataset.storefrontToken;
      this.endpoint = this.dataset.endpoint || '/api/2025-07/graphql.json';
      this.metaNamespace = this.dataset.metafieldNamespace || 'prd';
      this.metaKey = this.dataset.metafieldKey || 'all_details';
      this.imageNamespace = this.dataset.imageNamespace || 'custom';
      this.imageKey = this.dataset.imageKey || 'body_and_year_image';
      // Cache version comes from the merchant-editable "Vehicle Selector >
      // Cache Version" theme setting (settings.vehicle_selector_cache_version,
      // passed down as data-cache-version by the snippet) instead of a
      // hardcoded string. Bumping that setting changes this key, which
      // makes readCache() below miss on every customer's already-cached
      // entry (it's a different localStorage key now) and fall through to
      // a fresh fetch — a merchant-triggered cache bust with no deploy.
      this.cacheVersion = this.dataset.cacheVersion || 'v1';
      this.cacheKey = `c-vehicle-selector:${this.metaNamespace}.${this.metaKey}:${this.cacheVersion}`;
      this.cacheTtlMs = 6 * 60 * 60 * 1000; // 6 hours — catalog data doesn't change minute to minute.

      this.tree = null;
      this.state = { make: null, model: null, body: null, engine: null };
      this.labels = { make: null, model: null, body: null, engine: null };
      this.selectedProduct = null;
      this.currentStep = null; // one of STEP_ORDER, or 'go'

      this.triggerRow = this.querySelector('[data-vs-trigger-row]');
      this.trigger = this.querySelector('[data-vs-trigger]');
      this.sentinel = this.querySelector('[data-vs-sentinel]');
      this.stickyTrigger = this.querySelector('[data-vs-sticky-trigger]');
      this.stickyTriggerText = this.querySelector('[data-vs-sticky-trigger-text]');
      this.wizard = this.querySelector('[data-vs-wizard]');
      this.breadcrumbEl = this.querySelector('[data-vs-breadcrumb]');
      this.stageEl = this.querySelector('[data-vs-stage]');
      this.resetBtn = this.querySelector('[data-vs-reset]');
      this.statusEl = this.querySelector('[data-vs-status]');

      this.trigger.addEventListener('click', () => this.openWizard());
      if (this.stickyTrigger) {
        this.stickyTrigger.addEventListener('click', () => this.handleStickyTriggerClick());
      }
      this.resetBtn.addEventListener('click', () => this.reset());
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeOpenField();
      });

      this.setupStickyTrigger();
    }

    disconnectedCallback() {
      if (this.stickyObserver) this.stickyObserver.disconnect();
      window.clearTimeout(this._stickyScrollTimer);
    }

    // ---- mobile sticky trigger --------------------------------------------

    setupStickyTrigger() {
      // IntersectionObserver, not a scroll listener: the browser only
      // recomputes this when the observed sentinel actually crosses the
      // viewport edge, instead of running a handler on every scroll frame.
      if (!('IntersectionObserver' in window) || !this.sentinel || !this.stickyTrigger) return;

      this.updateStickyLabel();

      this.stickyObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          const shouldShow = !entry.isIntersecting;
          this.stickyTrigger.hidden = !shouldShow;
          this.stickyTrigger.tabIndex = shouldShow ? 0 : -1;
          if (shouldShow) this.updateStickyLabel();
        },
        { threshold: 0 },
      );
      this.stickyObserver.observe(this.sentinel);
    }

    // Reflects wizard progress: nothing picked yet vs. picked up mid-flow —
    // so scrolling back to a sticky bar that just says "Find Your Vehicle"
    // after you've already chosen a make doesn't read like it forgot.
    updateStickyLabel() {
      if (!this.stickyTriggerText) return;
      const hasProgress = STEP_ORDER.some((step) => this.labels[step]);
      this.stickyTriggerText.textContent = hasProgress ? 'Continue Selection' : 'Find Your Vehicle';
    }

    handleStickyTriggerClick() {
      // Scroll back to the selector's real (original) position first — the
      // sticky bar is pinned far from the hero, and opening/resuming the
      // wizard right where it's fixed would drop it in with no surrounding
      // context — then open once that settles. A fixed delay is simpler
      // and more robust here than trying to detect "the smooth scroll
      // finished". openWizard() -> ensureData() already resumes at
      // whatever step was last active instead of resetting (see its
      // early-return guard below), so this never loses the user's picks.
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.clearTimeout(this._stickyScrollTimer);
      this._stickyScrollTimer = window.setTimeout(() => this.openWizard(), 500);
    }

    // ---- open / reset ----------------------------------------------------

    openWizard() {
      this.classList.add('is-wizard-open');
      this.wizard.hidden = false;
      // Next frame so the hidden -> visible change is itself transitionable.
      requestAnimationFrame(() => this.wizard.classList.add('is-visible'));
      this.ensureData();
    }

    reset() {
      this.state = { make: null, model: null, body: null, engine: null };
      this.labels = { make: null, model: null, body: null, engine: null };
      this.selectedProduct = null;
      this.updateStickyLabel();
      if (this.tree) this.goToStep('make');
    }

    // ---- data lifecycle ----------------------------------------------------

    async ensureData() {
      if (this.tree) {
        if (!this.currentStep) this.goToStep('make');
        return;
      }

      this.setDataStatus('loading');
      this.stageEl.innerHTML = '<div class="c-vs__skeleton-bar"></div>';
      try {
        // Collection pages: the product list is already scoped and
        // rendered server-side (see snippets/c-vehicle-selector.liquid) —
        // no GraphQL call, no cache, just read the JSON island.
        const variants =
          this.dataSource === 'collection' ? this.readCollectionData() : await this.getGraphqlVariants();

        this.tree = this.buildTree(variants);
        this.setDataStatus('ready');
        this.goToStep('make');
      } catch (error) {
        console.error('[c-vehicle-selector]', error);
        this.setDataStatus('error');
      }
    }

    async getGraphqlVariants() {
      let variants = this.readCache();
      if (!variants) {
        variants = await this.fetchAllVariants();
        this.writeCache(variants);
      }
      return variants;
    }

    readCollectionData() {
      const script = this.querySelector('[data-vs-collection-data]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent) || [];
      } catch (error) {
        console.error('[c-vehicle-selector] Could not parse server-rendered collection data', error);
        return [];
      }
    }

    setDataStatus(status) {
      this.classList.remove('is-loading', 'is-error');
      if (status === 'loading') this.classList.add('is-loading');
      if (status === 'error') this.classList.add('is-error');
      if (this.statusEl) {
        this.statusEl.textContent =
          status === 'error' ? "Couldn't load vehicles right now. Please refresh and try again." : '';
      }
    }

    // ---- cache -------------------------------------------------------

    readCache() {
      try {
        const raw = localStorage.getItem(this.cacheKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.timestamp || !Array.isArray(parsed.variants)) return null;
        if (Date.now() - parsed.timestamp > this.cacheTtlMs) return null;
        return parsed.variants;
      } catch (error) {
        return null;
      }
    }

    writeCache(variants) {
      try {
        localStorage.setItem(this.cacheKey, JSON.stringify({ timestamp: Date.now(), variants }));
      } catch (error) {
        // Storage full or unavailable (private browsing, etc.) — fine to skip caching.
      }
    }

    // ---- data fetch ----------------------------------------------------

    async fetchAllVariants() {
      const variants = [];
      let cursor = null;
      let hasNextPage = true;
      let pageGuard = 0;

      while (hasNextPage && pageGuard < 40) {
        pageGuard += 1;

        const query = `{
          products(first: 250${cursor ? `, after: "${cursor}"` : ''}, query: "metafield:${this.metaNamespace}.${this.metaKey}*") {
            pageInfo { hasNextPage endCursor }
            nodes {
              handle
              detailsMeta: metafields(identifiers: [{ namespace: "${this.metaNamespace}", key: "${this.metaKey}" }]) {
                value
              }
              imageMeta: metafields(identifiers: [{ namespace: "${this.imageNamespace}", key: "${this.imageKey}" }]) {
                reference {
                  ... on MediaImage {
                    image { url }
                  }
                }
              }
              variants(first: 100) {
                nodes { id sku availableForSale }
              }
            }
          }
        }`;

        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.token,
          },
          body: JSON.stringify({ query }),
        });

        if (!response.ok) {
          throw new Error(`Storefront API responded with ${response.status}`);
        }

        const json = await response.json();
        if (json.errors) {
          throw new Error(json.errors.map((err) => err.message).join('; '));
        }

        const products = json.data.products;

        products.nodes.forEach((product) => {
          const meta = product.detailsMeta && product.detailsMeta[0];
          if (!meta || !meta.value) return;

          let details;
          try {
            details = JSON.parse(meta.value);
          } catch (error) {
            return;
          }

          const imageRef = product.imageMeta && product.imageMeta[0] && product.imageMeta[0].reference;
          const image = imageRef ? imageRef.image.url : '';

          product.variants.nodes.forEach((variant) => {
            variants.push({
              make: details.make,
              model: details.model,
              body: details.body,
              engineYear: details.engineYear,
              engineType: details.engineType,
              motorCode: details.motorCode,
              hp: details.hp,
              handle: product.handle,
              variantId: variant.id.split('/').pop(),
              sku: variant.sku,
              availableForSale: variant.availableForSale,
              image,
            });
          });
        });

        hasNextPage = products.pageInfo.hasNextPage;
        cursor = products.pageInfo.endCursor;
      }

      
      return variants;
    }

    buildTree(variants) {
      const tree = {};
      variants.forEach((variant) => {
        if (!variant.make || !variant.model || !variant.body || !variant.engineYear) return;
        tree[variant.make] = tree[variant.make] || {};
        tree[variant.make][variant.model] = tree[variant.make][variant.model] || {};
        tree[variant.make][variant.model][variant.body] = tree[variant.make][variant.model][variant.body] || {};
        const years = tree[variant.make][variant.model][variant.body];
        years[variant.engineYear] = years[variant.engineYear] || [];
        years[variant.engineYear].push(variant);
      });
      return tree;
    }

    // ---- option lists per step -------------------------------------------

    optionsForStep(step) {
      if (step === 'make') {
        return Object.keys(this.tree)
          .sort()
          .map((make) => ({ label: make, value: make }));
      }

      if (step === 'model') {
        const models = Object.keys(this.tree[this.state.make] || {}).sort();
        return models.map((model) => ({ label: model, value: model }));
      }

      if (step === 'body') {
        const bodies = this.tree[this.state.make][this.state.model] || {};
        const options = Object.entries(bodies).map(([body, years]) => {
          const { min, max } = getMinMaxYears(Object.keys(years));
          return { label: `${body} (${displayRange(min, max)})`, value: body };
        });
        options.sort((a, b) => a.label.localeCompare(b.label));
        return options;
      }

      // engine
      const years = this.tree[this.state.make][this.state.model][this.state.body] || {};
      const options = [];
      Object.entries(years).forEach(([year, entries]) => {
        entries.forEach((entry) => {
          options.push({
            label: `${entry.engineType} / ${entry.motorCode} / ${entry.hp} HP / ${year}`,
            value: entry.variantId,
            product: entry,
          });
        });
      });
      return options;
    }

    // ---- step transitions -------------------------------------------------

    goToStep(step) {
      this.currentStep = step;
      this.closeOpenField();
      this.renderBreadcrumb();
      this.transitionStage(step === 'go' ? this.goStageHtml() : this.fieldStageHtml(step));
    }

    transitionStage(html) {
      const stage = this.stageEl;

      const swapIn = () => {
        // BUG FIXED: this never cleared "is-leaving", so after the very
        // first transition the stage stayed permanently stuck matching
        // .c-vs__stage.is-leaving > * (opacity: 0) — every step after that
        // rendered real, correctly-colored <li> elements that were simply
        // invisible forever. Both leave/enter classes are mutually
        // exclusive, so clearing one when setting the other is required.
        stage.classList.remove('is-leaving');
        stage.innerHTML = html;
        this.bindStageEvents();
        stage.classList.add('is-entering');
        // Force a reflow so the browser registers the "entering" starting
        // point before we remove the class and let the transition run.
        void stage.offsetWidth;
        requestAnimationFrame(() => stage.classList.remove('is-entering'));
      };

      if (!stage.firstElementChild) {
        swapIn();
        return;
      }

      stage.classList.add('is-leaving');
      window.setTimeout(swapIn, STAGE_TRANSITION_MS);
    }

    fieldStageHtml(step) {
      const options = this.optionsForStep(step);
      const optionsHtml = options
        .map(
          (option, index) =>
            `<li class="c-vs__option" role="option" data-index="${index}">${escapeHtml(option.label)}</li>`,
        )
        .join('');

      this._pendingOptions = options;

      return `
        <div class="c-vs__field is-open">
          <button type="button" class="c-vs__field-btn" aria-haspopup="listbox" aria-expanded="true">
            <span class="c-vs__field-label">${escapeHtml(STEP_LABEL_SHORT[step])}</span>
            <span class="c-vs__field-value">${escapeHtml(STEP_LABEL[step])}</span>
            <svg class="c-vs__chevron" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <ul class="c-vs__listbox" role="listbox">${optionsHtml}</ul>
        </div>
      `;
    }

    goStageHtml() {
      const product = this.selectedProduct;
      const href = product ? `/products/${product.handle}?variant=${product.variantId}` : '#';
      return `
        <a href="${href}" class="c-vs__go-btn" data-vs-go>
          <span>Go To Product Page</span>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
      `;
    }

    bindStageEvents() {
      const goBtn = this.stageEl.querySelector('[data-vs-go]');
      if (goBtn) {
        goBtn.addEventListener('click', () => {
          // The href navigation itself isn't blocked (no preventDefault) —
          // this only covers the gap between click and the browser actually
          // leaving the page, so the tap gets an immediate response instead
          // of looking like nothing happened.
          if (goBtn.classList.contains('is-loading')) return;
          // Content (label + arrow) stays in the DOM untouched and just goes
          // transparent via CSS — swapping in "Loading..." text would
          // reflow the button to whatever width that string needs. The
          // spinner itself is a pure-CSS ::after, not an element added here.
          goBtn.classList.add('is-loading');
          goBtn.style.pointerEvents = 'none';
          goBtn.setAttribute('aria-disabled', 'true');
        });
        return; // go stage has nothing else to bind
      }

      const field = this.stageEl.querySelector('.c-vs__field');
      if (!field) return;

      const button = field.querySelector('.c-vs__field-btn');
      button.addEventListener('click', () => {
        const isOpen = field.classList.contains('is-open');
        field.classList.toggle('is-open', !isOpen);
        button.setAttribute('aria-expanded', String(!isOpen));
      });

      // Event delegation: one listener on the list itself instead of one
      // per <li> — holds up fine even if a make/model list runs into the
      // dozens of options.
      const list = field.querySelector('.c-vs__listbox');
      list.addEventListener('click', (event) => {
        const optionEl = event.target.closest('.c-vs__option');
        if (!optionEl || !list.contains(optionEl)) return;
        const option = this._pendingOptions[Number(optionEl.dataset.index)];
        this.selectOption(this.currentStep, option);
      });
    }

    closeOpenField() {
      const field = this.stageEl && this.stageEl.querySelector('.c-vs__field.is-open');
      if (!field) return;
      field.classList.remove('is-open');
      field.querySelector('.c-vs__field-btn').setAttribute('aria-expanded', 'false');
    }

    selectOption(step, option) {
      this.state[step] = option.value;
      this.labels[step] = option.label;
      if (step === 'engine') this.selectedProduct = option.product;
      this.updateStickyLabel();

      const idx = STEP_ORDER.indexOf(step);
      const nextStep = idx === STEP_ORDER.length - 1 ? 'go' : STEP_ORDER[idx + 1];
      this.goToStep(nextStep);
    }

    // ---- breadcrumb -------------------------------------------------------

    renderBreadcrumb() {
      const doneSteps = STEP_ORDER.filter((step) => this.labels[step]);
      const parts = doneSteps.map(
        (step) => `
          <span class="c-vs__crumb is-done">
            ${escapeHtml(this.labels[step])}
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        `,
      );

      if (this.currentStep !== 'go') {
        parts.push(`<span class="c-vs__crumb is-active">${escapeHtml(STEP_LABEL[this.currentStep])}…</span>`);
      } else {
        parts.push('<span class="c-vs__crumb is-active">Ready!</span>');
      }

      this.breadcrumbEl.innerHTML = parts
        .map((part, i) => (i === 0 ? part : `<span class="c-vs__crumb-sep">›</span>${part}`))
        .join('');
    }
  }

  customElements.define('c-vehicle-selector', VehicleSelector);
})();
